import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Zap, Mail, Lock, User } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://devisfast-backend.onrender.com/api';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'inscription') setActiveTab('signup');
  }, [searchParams]);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) { toast.error('Veuillez remplir tous les champs'); return; }
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Connexion réussie !');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signupName || !signupEmail || !signupPassword) { toast.error('Veuillez remplir tous les champs'); return; }
    if (signupPassword !== signupConfirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (signupPassword.length < 6) { toast.error('Le mot de passe doit contenir au moins 6 caractères'); return; }
    if (!acceptTerms) { toast.error('Vous devez accepter les CGU et la Politique de Confidentialité'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/signup`, {
        email: signupEmail,
        password: signupPassword,
        full_name: signupName
      });
      await login(signupEmail, signupPassword);
      toast.success('Bienvenue sur DevisFast ! Votre essai 14 jours est lancé 🚀');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 noise-overlay">
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Zap className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight uppercase">DevisFast</h1>
          <p className="text-muted-foreground mt-2">Vos soirées appartiennent à votre famille, pas à vos devis.</p>
        </div>

        <Card className="bg-card border-white/10">
          <CardHeader className="pb-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-muted/50">
                <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
                <TabsTrigger value="signup" data-testid="signup-tab">Inscription</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {activeTab === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="login-email" type="email" placeholder="votre@email.fr" value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
                      data-testid="login-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="login-password" type="password" placeholder="••••••••" value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
                      data-testid="login-password-input" />
                  </div>
                </div>
                <Button type="submit"
                  className="w-full h-12 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
                  disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
                <div className="text-center">
                  <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    data-testid="forgot-password-link">Mot de passe oublié ?</Link>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Nom complet</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="signup-name" type="text" placeholder="Jean Dupont" value={signupName}
                      onChange={(e) => setSignupName(e.target.value)}
                      className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
                      data-testid="signup-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="signup-email" type="email" placeholder="votre@email.fr" value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
                      data-testid="signup-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="signup-password" type="password" placeholder="••••••••" value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
                      data-testid="signup-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-confirm">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input id="signup-confirm" type="password" placeholder="••••••••" value={signupConfirm}
                      onChange={(e) => setSignupConfirm(e.target.value)}
                      className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
                      data-testid="signup-confirm-input" />
                  </div>
                </div>
                <div className="flex items-start space-x-3 py-2">
                  <Checkbox id="accept-terms" checked={acceptTerms} onCheckedChange={setAcceptTerms}
                    className="mt-0.5" data-testid="accept-terms-checkbox" />
                  <label htmlFor="accept-terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                    J'accepte les{' '}
                    <Link to="/cgu" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>CGU</Link>
                    {' '}et la{' '}
                    <Link to="/privacy" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>Politique de Confidentialité</Link>
                    {' '}de DevisFast
                  </label>
                </div>
                <Button type="submit"
                  className="w-full h-12 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
                  disabled={loading || !acceptTerms} data-testid="signup-submit-btn">
                  {loading ? 'Création...' : 'Créer mon compte →'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-sm text-muted-foreground mt-6">
          Devis en 3 min · Facture en 1 clic · Signature électronique · 9,99€/mois · Essai 14 jours gratuit
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
